import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { PaidPayrollTab } from '../components/payroll/PaidPayrollTab';
import { UnpaidPayrollTab } from '../components/payroll/UnpaidPayrollTab';
import { payrollStatusLabel } from '../components/payroll/payrollStatusLabels';
import { usePayrollAccess } from '../components/payroll/usePayrollAccess';
import { visibleAdminSections } from '../constants/adminSections';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';
import { formatTabCount } from '../components/billing/formatTabCount';
import {
  getPayrollSettings,
  listPayrollEntries,
  listPayrollVisits,
} from '../services/api';
import type { TaskStatus } from '../services/api/types.gen';
import { useStore } from '../store';
import type { PayrollFilters } from '../store/slices/payrollSlice';

type PayrollView = 'unpaid' | 'paid';

const VIEW_TITLES: Record<PayrollView, string> = {
  unpaid: 'Unpaid Visits',
  paid: 'Paid Payrolls',
};

const PAYROLL_TABS: {
  id: PayrollView;
  label: string;
  countKey: 'unpaid' | 'paid';
}[] = [
  { id: 'unpaid', label: 'Unpaid', countKey: 'unpaid' },
  { id: 'paid', label: 'Paid', countKey: 'paid' },
];

type TabCounts = {
  unpaid: number | null;
  paid: number | null;
};

/** Same required fields the Unpaid tab needs before it can list visits. */
function unpaidCountQuery(filters: PayrollFilters) {
  if (!filters.clinicId || !filters.startDate || !filters.endDate) return null;
  return {
    clinic_id: filters.clinicId,
    start_date: filters.startDate,
    end_date: filters.endDate,
    employee_types:
      filters.employeeTypes.length > 0 ? filters.employeeTypes : undefined,
    search: filters.search || undefined,
    hide_imported_patients: filters.hideImportedPatients !== false,
    exclude_pre_takeover: filters.excludePreTakeover !== false,
    page: 1,
    per_page: 1,
  };
}

/** Payroll Manager shell: Unpaid / Paid tabs (Billing Manager pattern). */
const PayrollManager: React.FC = () => {
  // Same rule the sidebar entry gates on, so a bookmarked or typed-in URL
  // can't render the screen for someone the API would refuse anyway.
  const hasAccess = usePayrollAccess();
  const agencyId = useStore(state => state.user?.agency?.id);
  const payrollFilters = useStore(state => state.payrollFilters);
  // The link appears exactly when the Admin tab it opens does.
  const canOpenPayrollSettings = useStore(state =>
    visibleAdminSections(
      state.hasAnyPermission,
      state.user?.agency?.is_rogeremr_enabled === true,
      state.hasFeatureFlag
    ).some(section => section.id === 'payroll')
  );

  // The visit statuses the Unpaid tab holds. The agency's setting until a list
  // loads, then the set each list response was selected with, so the status line
  // never describes rows other than the ones on screen.
  const [unpaidStatuses, setUnpaidStatuses] = useState<TaskStatus[] | null>(
    null
  );
  // Claimed by each settings read when it starts, and bumped by each list
  // that reports: a settings answer landing after a list's is older news.
  const statusesVersionRef = useRef(0);
  const [tabCounts, setTabCounts] = useState<TabCounts | null>(null);
  const tabCountsGenerationRef = useRef(0);

  useEffect(() => {
    if (!hasAccess || !agencyId) return;
    const version = ++statusesVersionRef.current;
    setUnpaidStatuses(null);
    getPayrollSettings({ throwOnError: true })
      .then(response => {
        if (version === statusesVersionRef.current) {
          setUnpaidStatuses(response.data.eligible_statuses);
        }
      })
      // No toast: the status line is left out, and nothing on the tab
      // depends on it.
      .catch(error => console.error('Failed to load payroll settings:', error));
  }, [hasAccess, agencyId]);

  const handleStatusesLoaded = useCallback((statuses: TaskStatus[]) => {
    statusesVersionRef.current += 1;
    setUnpaidStatuses(statuses);
  }, []);

  const fetchTabCounts = useCallback(async () => {
    if (!hasAccess || !agencyId) return;
    const generation = ++tabCountsGenerationRef.current;
    const unpaidQuery = unpaidCountQuery(payrollFilters);
    const [unpaidRes, paidRes] = await Promise.all([
      unpaidQuery
        ? listPayrollVisits({ query: unpaidQuery })
        : Promise.resolve(null),
      listPayrollEntries({
        query: {
          page: 1,
          per_page: 1,
          hide_imported_patients: payrollFilters.hideImportedPatients !== false,
        },
      }),
    ]);
    if (generation !== tabCountsGenerationRef.current) return;
    setTabCounts({
      unpaid:
        unpaidRes && !unpaidRes.error && unpaidRes.data
          ? unpaidRes.data.total
          : null,
      paid: !paidRes.error && paidRes.data ? paidRes.data.total : null,
    });
  }, [hasAccess, agencyId, payrollFilters]);

  useEffect(() => {
    void fetchTabCounts();
  }, [fetchTabCounts]);

  // Kept in the URL so the detail screen can navigate back to the Paid tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView: PayrollView =
    searchParams.get('tab') === 'paid' ? 'paid' : 'unpaid';
  useBreadcrumbs(hasAccess ? [{ label: VIEW_TITLES[activeView] }] : []);

  const handleViewChange = useCallback(
    (value: string) => {
      setSearchParams(value === 'paid' ? { tab: 'paid' } : {}, {
        replace: true,
      });
    },
    [setSearchParams]
  );

  if (!hasAccess) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access Payroll Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeView}
        onValueChange={handleViewChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        {/* Tabs on the left like NOA / OASIS; status copy sits beside them. */}
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <TabsList className='max-sm:h-auto max-sm:flex-wrap'>
            {PAYROLL_TABS.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id}>
                <span className='inline-flex items-center gap-x-1'>
                  <span>{tab.label}</span>
                  <TabsCount
                    label={tab.label}
                    value={formatTabCount(tabCounts?.[tab.countKey])}
                  />
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {activeView === 'unpaid' && unpaidStatuses && (
            <p className='min-w-0 text-sm text-muted-foreground'>
              Unpaid visits in:{' '}
              {unpaidStatuses
                .map(status => payrollStatusLabel(status))
                .join(', ')}
              {canOpenPayrollSettings && (
                <>
                  {' · '}
                  <Link
                    to='/admin?section=payroll'
                    className='whitespace-nowrap font-medium text-primary underline-offset-2 hover:underline'
                  >
                    Change in Admin
                  </Link>
                </>
              )}
            </p>
          )}
        </div>

        <TabsContent value='unpaid' className='m-0'>
          <UnpaidPayrollTab onStatusesLoaded={handleStatusesLoaded} />
        </TabsContent>

        <TabsContent value='paid' className='m-0'>
          <PaidPayrollTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollManager;
